import React, { PropsWithChildren, Ref, useMemo } from 'react';

import PropTypes from 'prop-types';

import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import {
  TABLE_ROW_SELECTABLE_CLASSNAME,
  TABLE_ROW_SELECTED_CLASSNAME,
  TABLE_SIZES,
} from '../consts';
import useTableSize from '../hooks/useTableSize';
import { Themeable } from '../types';

export type TableRowProps = StylableComponent<HTMLDivElement> &
  Themeable & {
    disabled?: boolean;
    canSelect?: boolean;
    toggleSelected?: (value?: boolean, e?: React.MouseEvent) => void;
    isSelected?: boolean;
    isHighlightable?: boolean;
  };

const TableRow = React.forwardRef(
  (
    {
      children,
      canSelect,
      toggleSelected,
      isSelected,
      isHighlightable: isHighlightableProp,
      disabled,
      tableSize,
      style,
      className: classNameProp,
      ...props
    }: PropsWithChildren<TableRowProps>,
    ref: Ref<HTMLTableRowElement>,
  ) => {
    const tableSizeTheme = useTableSize(tableSize);
    const theme = useTheme();
    const rowTokens = theme.tokens?.component?.table?.row;

    const isHighlightable =
      isHighlightableProp !== undefined ? isHighlightableProp : canSelect;

    const className = useMemo(() => {
      const classNames = [classNameProp];

      if (canSelect) {
        classNames.push(TABLE_ROW_SELECTABLE_CLASSNAME);
      }

      if (isSelected) {
        classNames.push(TABLE_ROW_SELECTED_CLASSNAME);
      }

      return classNames.join(' ');
    }, [canSelect, classNameProp, isSelected]);

    const selectedBase = rowTokens?.state?.selected;
    const selectedHover = selectedBase?.['&:hover'];

    const baseCss = useMemo(
      () => ({
        display: 'flex',
        height: tableSizeTheme.rowHeight,
        minWidth: 'fit-content',
      }),
      [tableSizeTheme.rowHeight],
    );

    const highlightStyles = useMemo(
      () =>
        isHighlightable && !disabled
          ? {
              "& > [role='cell']": isSelected ? selectedBase : rowTokens?._,
              '&:hover': {
                "& > [role='cell']":
                  isSelected && selectedHover
                    ? selectedHover
                    : rowTokens?.state?.hover,
              },
            }
          : undefined,
      [
        isHighlightable,
        disabled,
        isSelected,
        selectedBase,
        selectedHover,
        rowTokens,
      ],
    );

    const selectableStyles = useMemo(
      () => ({
        cursor: rowTokens?.selectable?.[canSelect ? 'true' : 'false']?.cursor,
        ...(canSelect && {
          '&:active': {
            "& > [role='cell']": rowTokens?.state?.pressed,
          },
        }),
      }),
      [canSelect, rowTokens],
    );

    const disabledStyles = useMemo(
      () =>
        disabled
          ? {
              color: rowTokens?.state?.disabled?.color,
              "& > [role='cell']": {
                color: rowTokens?.state?.disabled?.color,
              },
            }
          : undefined,
      [disabled, rowTokens],
    );

    return (
      <tr
        onClick={(e: React.MouseEvent) =>
          !disabled && canSelect && toggleSelected?.(!isSelected, e)
        }
        ref={ref}
        className={className}
        css={[
          baseCss,
          highlightStyles,
          selectableStyles,
          disabledStyles,
          style,
        ]}
        {...(disabled ? { 'data-disabled': true, 'aria-disabled': true } : {})}
        {...props}
      >
        {children}
      </tr>
    );
  },
);

TableRow.displayName = 'TableRow';

TableRow.propTypes = {
  tableSize: PropTypes.oneOf(Object.values(TABLE_SIZES)),
  canSelect: PropTypes.bool,
  toggleSelect: PropTypes.func,
  isSelected: PropTypes.bool,
  children: PropTypes.any,
  style: stylePropType,
};

export default TableRow;
